import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, apiFetch } from '../../api/client';
import { SidePanel } from '../../components/SidePanel';

type Role = 'ADMIN' | 'TEACHER';
type Entry = { email: string; role: Role; createdAt: string };

const ROLE_LABEL: Record<Role, string> = { ADMIN: 'Beheerder', TEACHER: 'Docent' };

function messageFor(error: unknown, action: 'add' | 'remove'): string {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return action === 'add' ? 'Dit adres staat al in de lijst.' : 'Je kunt jezelf en de laatste beheerder niet verwijderen.';
    }
    if (error.status === 400) return 'Dat is geen geldig e-mailadres.';
  }
  return 'Er ging iets mis. Probeer het opnieuw.';
}

export default function AccessPanel() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('TEACHER');

  const entries = useQuery({
    queryKey: ['access'],
    queryFn: async () => (await (await apiFetch('/api/admin/access')).json()) as Entry[],
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['access'] });

  const add = useMutation({
    mutationFn: () =>
      apiFetch('/api/admin/access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role }),
      }),
    onSuccess: () => {
      setEmail('');
      return refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (address: string) => apiFetch(`/api/admin/access/${encodeURIComponent(address)}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (email.trim()) add.mutate();
  }

  return (
    <SidePanel title="Toegang" description="Alleen deze Google-adressen kunnen inloggen. Een nieuw adres krijgt een eigen, lege planner.">
      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          E-mailadres
          <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="off" className="min-h-11" />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Rol
          <select value={role} onChange={(event) => setRole(event.target.value as Role)} className="min-h-11 rounded-lg border bg-transparent px-2">
            <option value="TEACHER">Docent</option>
            <option value="ADMIN">Beheerder</option>
          </select>
        </label>
        <Button type="submit" className="min-h-11 self-start" disabled={add.isPending}>
          Toevoegen
        </Button>
      </form>

      {(add.error ?? remove.error) && (
        <p role="alert" className="text-sm">
          {add.error ? messageFor(add.error, 'add') : messageFor(remove.error, 'remove')}
        </p>
      )}

      {entries.isPending ? (
        <p>Laden…</p>
      ) : entries.isError ? (
        <p role="alert">De lijst kon niet worden geladen. Vernieuw de pagina.</p>
      ) : (
        <ul className="flex flex-col divide-y">
          {entries.data.map((entry) => (
            <li key={entry.email} className="flex items-center justify-between gap-3 py-2">
              <span className="min-w-0 break-words">
                {entry.email} <span className="text-sm opacity-70">({ROLE_LABEL[entry.role]})</span>
              </span>
              <Button variant="ghost" className="min-h-11" aria-label={`Verwijder ${entry.email}`} onClick={() => remove.mutate(entry.email)}>
                Verwijderen
              </Button>
            </li>
          ))}
        </ul>
      )}
    </SidePanel>
  );
}
